import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

import api from "../../api/api";
import endpoint from "../../api/endpoints";
import { getCurrentPrice } from "../../config/config";
import { STATUS } from "../status";

// Async thunk to calculate price after discount
export const calculatePriceAfterDiscount = createAsyncThunk(
  "itemPriceAfterDiscount/calculatePriceAfterDiscount",
  async (
    { productId, unitPrice, quantity, customerId },
    { rejectWithValue }
  ) => {
    try {
      // Fetch promotions for the product
      const promotionUrl = endpoint.GET_PROMOTIONS_BY_PRODUCT({
        productId,
      });

      const response = await api.get(promotionUrl);
      const promotions = response.data;

      if (!promotions || promotions.length === 0) {
        return {
          productId,
          unitPrice,
          quantity,
          originalPrice: Number(unitPrice) * Number(quantity),
          discountedPrice: Number(unitPrice) * Number(quantity),
          discount: 0,
          promotionApplied: null,
        };
      }

      const originalTotal = Number(unitPrice) * Number(quantity);
      let bestDiscount = 0;
      let bestPromotion = null;

      // Check each promotion to find the best discount
      for (const promotion of promotions) {
        const { fixedprice, itemquantifier, customercategory } = promotion;

        // Skip if promotion doesn't have fixedprice
        if (!fixedprice) continue;

        // Check if customer is eligible for this promotion based on customercategory
        if (customercategory) {
          // customercategory is a comma-separated string like "11, 12, 55"
          const allowedCategories = customercategory
            .split(",")
            .map((cat) => cat.trim());
          // If customer is not in the allowed categories, skip this promotion
          if (!allowedCategories.includes(String(customerId))) {
            continue;
          }
        }
        // If customercategory is null/empty, promotion applies to everyone

        // Check if quantity meets the minimum requirement
        // If no itemquantifier is specified, apply the promotion (no minimum requirement)
        const minQuantity = itemquantifier ? Number(itemquantifier) : 0;

        if (Number(quantity) >= minQuantity) {
          // Calculate discounted price: fixedprice * quantity
          const discountedTotal = Number(fixedprice) * Number(quantity);
          const discount = originalTotal - discountedTotal;

          // Keep track of best discount
          if (discount > bestDiscount) {
            bestDiscount = discount;
            bestPromotion = promotion;
          }
        }
      }

      return {
        productId,
        unitPrice,
        quantity,
        originalPrice: originalTotal,
        discountedPrice: originalTotal - bestDiscount,
        discount: bestDiscount,
        promotionApplied: bestPromotion,
      };
    } catch (error) {
      // Return original price if error occurs
      const originalTotal = Number(unitPrice) * Number(quantity);
      return rejectWithValue({
        productId,
        unitPrice,
        quantity,
        originalPrice: originalTotal,
        discountedPrice: originalTotal,
        discount: 0,
        promotionApplied: null,
        error: error.message,
      });
    }
  }
);

const itemPriceAfterDiscountSlice = createSlice({
  name: "itemPriceAfterDiscount",
  initialState: {
    // Store price data by productId
    priceData: {},
    status: STATUS.IDLE,
    error: null,
  },
  reducers: {
    // Clear price data for a specific product
    clearProductPriceData: (state, action) => {
      const { productId } = action.payload;
      delete state.priceData[productId];
    },
    // Clear all price data
    clearAllPriceData: (state) => {
      state.priceData = {};
      state.status = STATUS.IDLE;
      state.error = null;
    },
    // Update price data directly (for manual updates)
    updatePriceData: (state, action) => {
      const { productId, priceInfo } = action.payload;
      state.priceData[productId] = priceInfo;
    },
    // Check if price data should be recalculated
    shouldRecalculatePrice: (state, action) => {
      const { productId, unitPrice, quantity } = action.payload;
      const priceKey = `${productId}-${unitPrice}-${quantity}`;
      return !state.priceData[priceKey];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(calculatePriceAfterDiscount.pending, (state) => {
        state.status = STATUS.LOADING;
        state.error = null;
      })
      .addCase(calculatePriceAfterDiscount.fulfilled, (state, action) => {
        state.status = STATUS.SUCCEEDED;
        const {
          productId,
          unitPrice,
          quantity,
          originalPrice,
          discountedPrice,
          discount,
          promotionApplied,
        } = action.payload;

        // Create a unique key for the product with its price and quantity
        const priceKey = `${productId}-${unitPrice}-${quantity}`;

        state.priceData[priceKey] = {
          productId,
          unitPrice,
          quantity,
          originalPrice,
          discountedPrice,
          discount,
          promotionApplied,
          calculatedAt: new Date().toISOString(),
        };
      })
      .addCase(calculatePriceAfterDiscount.rejected, (state, action) => {
        state.status = STATUS.FAILED;
        state.error = action.payload;
      });
  },
});

export const {
  clearProductPriceData,
  clearAllPriceData,
  updatePriceData,
  shouldRecalculatePrice,
} = itemPriceAfterDiscountSlice.actions;

export default itemPriceAfterDiscountSlice.reducer;

// Selectors
export const selectPriceDataByKey = (state, productId, unitPrice, quantity) => {
  const priceKey = `${productId}-${unitPrice}-${quantity}`;
  return state.itemPriceAfterDiscount.priceData[priceKey];
};

export const selectAllPriceData = (state) =>
  state.itemPriceAfterDiscount.priceData;

export const selectPriceStatus = (state) => state.itemPriceAfterDiscount.status;

export const selectPriceError = (state) => state.itemPriceAfterDiscount.error;

export const selectHasDiscount = (state, productId, unitPrice, quantity) => {
  const priceData = selectPriceDataByKey(state, productId, unitPrice, quantity);
  return priceData && priceData.discount > 0;
};

export const selectDiscountAmount = (state, productId, unitPrice, quantity) => {
  const priceData = selectPriceDataByKey(state, productId, unitPrice, quantity);
  return priceData ? priceData.discount : 0;
};

export const selectFinalPrice = (state, productId, unitPrice, quantity) => {
  const priceData = selectPriceDataByKey(state, productId, unitPrice, quantity);
  return priceData ? priceData.discountedPrice : unitPrice * quantity;
};

// Selector to check if price data exists for a specific combination
export const selectPriceDataExists = (
  state,
  productId,
  unitPrice,
  quantity
) => {
  const priceKey = `${productId}-${unitPrice}-${quantity}`;
  return !!state.itemPriceAfterDiscount.priceData[priceKey];
};

// Selector to calculate total cart subtotal with discounts
export const selectCartSubtotalWithDiscounts = (state, cartItems) => {
  return cartItems.reduce((total, item) => {
    const pricingInfo = getCurrentPrice(item, item.quantity);
    return total + pricingInfo.currentPrice * item.quantity;
  }, 0);
};
